"use client";

import React, {
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { PopoverTrigger } from "@nextui-org/react";
import delay from "lodash/delay";
import { useTranslations } from "next-intl";
import { useMediaQuery } from "react-responsive";

import { useAutocompleteFilteredOptions } from "@/hooks/useAutocompleteLocationOptions";
import { Popover, PopoverContent } from "@/ui/SearchPanel/SearchPopover";
import { cn } from "@/utils/cn";

import { type AutocompleteItem, AutocompleteList } from "../AutocompleteList";
import SmartAutocompleteInput from "../SmartAutocompleteInput";

interface FormSmartAutocompleteProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  options?: AutocompleteItem[];
  emptyBlock?: React.ReactNode;
  listTitle?: string;
  noResultsBlock?: React.ReactNode;
  activePopUp: string;
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  inputLocationRef: Ref<HTMLInputElement>;
}

const minSymbolsToTrigger = 1;

const FormSmartAutocomplete: React.FC<FormSmartAutocompleteProps> = ({
  placeholder = "Enter location...",
  value: initialValue = "",
  onChange,
  onClear,
  options = [],
  emptyBlock,
  listTitle = "Choose location",
  noResultsBlock = <div className="p-4 text-default-500">No results found</div>,
  activePopUp,
  isOpen,
  onOpenChange,
  inputLocationRef,
}) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const touchHandled = useRef<boolean>(false);
  const isClearing = useRef<boolean>(false);
  const t = useTranslations();

  const isMobile = useMediaQuery({
    query: "(max-width: 767px)",
  });

  const filteredOptions = useAutocompleteFilteredOptions(options, value);

  const handleChange = (val: string) => {
    setValue(val);
  };

  const handleClear = () => {
    // Set flag to prevent popover from opening
    isClearing.current = true;

    // Close popover if it's open
    onOpenChange?.(false);

    // Clear the value
    setValue("");

    // Call parent's onClear
    onClear?.();

    // Reset flag after a short delay
    delay(() => {
      isClearing.current = false;
    }, 100);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // Store the initial touch position
    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    touchHandled.current = false;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Don't open popover if we're clearing
    if (isClearing.current) return;

    // Only open if it's a tap, not a swipe
    if (!touchStartPos.current) return;

    const touchEndPos = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    };

    // Calculate distance moved
    const deltaX = Math.abs(touchEndPos.x - touchStartPos.current.x);
    const deltaY = Math.abs(touchEndPos.y - touchStartPos.current.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Threshold for considering it a tap (10px tolerance)
    const tapThreshold = 10;

    if (distance <= tapThreshold) {
      // It's a tap, not a swipe - open the popover
      if (!isOpen && onOpenChange) {
        onOpenChange(true);
        touchHandled.current = true;

        // Prevent click event from firing after touch
        e.preventDefault();
      }
    }

    // Reset touch position after a short delay
    setTimeout(() => {
      touchStartPos.current = null;
      touchHandled.current = false;
    }, 300);
  };

  const handleClick = () => {
    // Don't open popover if we're clearing
    if (isClearing.current) return;

    // Don't handle click if touch was already handled
    if (touchHandled.current) return;

    if (!isOpen && onOpenChange) {
      onOpenChange(true);
    }
  };

  const handleAutocompleteSelect = (item: AutocompleteItem) => {
    setValue(
      initialValue === item.valueCurrentLocale
        ? item.valueCurrentLocale
        : item.valueLocalized,
    );
    onChange?.(item.value);
    onOpenChange?.(false);
    inputRef.current?.blur();
    // inputRef.current?.focus();
  };

  const showList = useMemo(() => {
    return (
      value.length >= minSymbolsToTrigger &&
      filteredOptions.length > 0 &&
      !isMobile
    );
  }, [filteredOptions, isMobile, value]);

  const handleSetInputFocus = useCallback(() => {
    if (isOpen && !isMobile) {
      setIsFocused(true);
      delay(() => inputRef?.current?.focus(), 0);
    } else if (activePopUp !== "" || !isOpen) {
      setIsFocused(false);
      delay(() => inputRef?.current?.blur(), 0);
    }
  }, [activePopUp, isMobile, isOpen]);

  useEffect(() => {
    handleSetInputFocus();
  }, [handleSetInputFocus]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={
        isMobile
          ? undefined
          : (isOpen) => {
              if (isFocused) {
                onOpenChange?.(isOpen);
              }
            }
      }
      showArrow={false}
      offset={isMobile ? 0 : 20}
      variant={isMobile ? "fullScreen" : "search"}
    >
      <PopoverTrigger>
        <div
          className="w-full md:min-w-[350px] lg:min-w-[470px] h-full bg-white rounded-[15px] box-content outline outline-1 outline-transparent aria-expanded:outline-red"
          ref={inputLocationRef}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <SmartAutocompleteInput
            value={value}
            onChange={handleChange}
            onClear={handleClear}
            onKeyPress={(e) => {
              const fullCoincidence = filteredOptions.find(
                (item) =>
                  item.title.trim().toLowerCase() ===
                  value.trim().toLowerCase(),
              );

              if (
                e.key === "Enter" &&
                (filteredOptions.length === 1 || fullCoincidence)
              ) {
                handleAutocompleteSelect(fullCoincidence ?? filteredOptions[0]);
              }
            }}
            placeholder={
              isOpen && !isFocused
                ? `${t("Enter location name")}...`
                : placeholder
            }
            isOpen={isOpen}
            inputRef={inputRef}
          />
        </div>
      </PopoverTrigger>

      <PopoverContent
        variant={isMobile ? "fullScreen" : "search"}
        className={cn(
          "pointer-events-auto lg:!max-w-[min(calc(100dvw-20rem),1310px)]",
          isMobile && "justify-start",
        )}
      >
        {showList ? (
          <div className="w-full">
            <AutocompleteList
              items={filteredOptions}
              searchValue={value}
              listTitle={listTitle}
              onItemClick={handleAutocompleteSelect}
              noResultsMessage={noResultsBlock}
            />
          </div>
        ) : (
          emptyBlock
        )}
      </PopoverContent>
    </Popover>
  );
};

export default FormSmartAutocomplete;
